// MasonryGrid.jsx
import React, { useEffect, useRef } from "react";
import Masonry from "react-masonry-css";
import { Link } from "react-router-dom";
/**
 * Sample items array containing image URLs and corresponding text.
 * Modify this array to include your own images and texts.
 */
const items = [
  {
    id: 1,
    src: "/MasonGrid/1.png",
    text: "T-Shirt",
    link: "/Garments/TShirt",
  },
  {
    id: 2,
    src: "/MasonGrid/2.png",
    text: "Hoodie",
    link: "/Garments/Hoodie",
  },
  {
    id: 3,
    src: "/MasonGrid/3.png",
    text: "Sweatshirts",
    link: "/Garments/Sweatshirts",
  },
  {
    id: 4,
    src: "/MasonGrid/4.png",
    text: "Sweat Pants",
    link: "/Garments/SweatPants",
  },
  {
    id: 5,
    src: "/MasonGrid/7.png",
    text: "Leggings",
    link: "/Garments/Leggings",
  },
  {
    id: 6,
    src: "/MasonGrid/9.png",
    text: "Joggers",
    link: "/Garments/Joggers",
  },
  {
    id: 7,
    src: "/MasonGrid/11.png",
    text: "Kids T-Shirt",
    link: "/Garments/KidsTee",
  },
  {
    id: 8,
    src: "/MasonGrid/14.png",
    text: "Rompers",
    link: "/Garments/Rompers",
  },
  {
    id: 9,
    src: "/MasonGrid/15.png",
    text: "Onesizes",
    link: "/Garments/Onesizes",
  },

  {
    id: 11,
    src: "/MasonGrid/12.png",
    text: "Bibs",
    link: "/Garments/Bibs",
  },
  {
    id: 12,
    src: "/MasonGrid/10.png",
    text: "Pyjamas",
    link: "/Garments/Pyjamas",
  },
];

/**
 * MasonryGrid Component
 *
 * Renders a responsive masonry grid with images and centered text.
 * Ensures the last row is balanced by adding placeholder items.
 * Includes functionality to block zooming within the component.
 */
const MasonryGrid = () => {
  // Reference to the main grid container for attaching event listeners
  const gridRef = useRef(null);

  /**
   * useEffect Hook
   *
   * Sets up event listeners to prevent zooming via keyboard and touch within the component.
   * Cleans up listeners on component unmount.
   */
  useEffect(() => {
    const gridElement = gridRef.current;

    /**
     * Handler to prevent zoom via keyboard (Ctrl +, Ctrl -, Ctrl 0)
     * @param {KeyboardEvent} e - The keyboard event
     */
    const handleKeyDown = (e) => {
      if (
        (e.ctrlKey || e.metaKey) && // Detects Ctrl (Windows) or Cmd (Mac)
        (e.key === "+" || e.key === "-" || e.key === "0") // Detects +, -, or 0 keys
      ) {
        e.preventDefault(); // Prevents the default zoom action
      }
    };

    /**
     * Handler to prevent pinch-to-zoom on touch devices
     * @param {TouchEvent} e - The touch event
     */
    const handleTouchMove = (e) => {
      if (e.touches.length > 1) {
        e.preventDefault(); // Prevents pinch-to-zoom gestures
      }
    };

    // Attach event listeners if the grid element is present
    if (gridElement) {
      gridElement.addEventListener("keydown", handleKeyDown, {
        passive: false,
      });
      gridElement.addEventListener("touchmove", handleTouchMove, {
        passive: false,
      });
    }

    // Cleanup event listeners on component unmount to prevent memory leaks
    return () => {
      if (gridElement) {
        gridElement.removeEventListener("keydown", handleKeyDown);
        gridElement.removeEventListener("touchmove", handleTouchMove);
      }
    };
  }, []); // Empty dependency array ensures this runs once on mount

  /**
   * Define breakpoints for responsive design.
   *
   * The keys represent the minimum screen width (in pixels) at which the corresponding
   * number of columns should be applied.
   *
   * - **default:** 3 columns for medium screens and above (≥768px)
   * - **768:** 2 columns for small screens (<768px)
   *
   * **Customization:**
   * - Modify the numbers to change the column counts at different breakpoints.
   * - Add more breakpoints as needed for finer control.
   */
  const breakpointColumnsObj = {
    default: 3, // 3 columns for medium screens and above
    768: 2, // 2 columns for small screens
  };

  /**
   * Calculate the number of placeholder items needed to balance the last row.
   * Ensures that the total number of items is divisible by the number of columns.
   */
  const placeholdersNeeded =
    (breakpointColumnsObj.default -
      (items.length % breakpointColumnsObj.default)) %
    breakpointColumnsObj.default;

  return (
    <div
      ref={gridRef} // Attach the ref to the main container for event listeners
      className="
        p-4          
        md:px-32     
        lg:px-48
        
      "
      tabIndex={0} // Makes the div focusable to capture keyboard events
      style={{ userSelect: "none" }} // Ensures user selection is disabled via inline style
    >
      {/* 
        react-masonry-css requires specific class names for the grid and columns.
        We define these class names and their corresponding styles within the <style> tag below.
      */}
      <Masonry
        breakpointCols={breakpointColumnsObj} // Defines the number of columns based on screen width
        className="my-masonry-grid" // Custom class name for the grid
        columnClassName="my-masonry-grid_column" // Custom class name for the columns
      >
        {/* 
          Iterate over the items array and render each item within the masonry grid.
          Each item consists of an image and centered text below it.
        */}
        {items.map((item) => (
          <a href={item.link} key={item.id} className="mb-4 break-inside-avoid">
            <div key={item.id} className="mb-4 break-inside-avoid">
              {/* 
              Image Container with Aspect Ratio
              Ensures consistent aspect ratios for images to promote balanced columns.
            */}
              <div className="aspect-w-16 aspect-h-9">
                {" "}
                {/* 16:9 Aspect Ratio */}
                <img
                  src={item.src}
                  alt={`Photo titled: ${item.text}`} // Descriptive alt text for accessibility
                  className="
                  w-full       /* Makes the image take the full width of its container */
                  h-full       /* Makes the image take the full height of its container */
                  rounded-xl   /* Adds extra-large rounded corners to the image */
                  object-cover  /* Ensures the image covers its container without distortion */
                  transition    /* Adds transition for smooth hover effects */
                  duration-300  /* Duration of the transition */
                  ease-in-out   /* Timing function for the transition */
                  hover:scale-105 /* Slightly enlarges the image on hover */
                  hover:shadow-lg  /* Adds a shadow on hover */
                 
                "
                  draggable={false} // Prevents the image from being dragged
                  loading="lazy" // Enables lazy loading for performance optimization
                  onLoad={(e) => e.target.classList.add("loaded")} // Adds 'loaded' class on load
                  onError={(e) => {
                    e.target.src =
                      "https://via.placeholder.com/300x200?text=Image+Not+Found";
                    e.target.alt = "Image Not Found";
                  }} // Fallback for failed image loads
                />
              </div>
              <p className="mt-2 text-center  font-bold">{item.text}</p>{" "}
              {/* Centered text below the image */}
            </div>
          </a>
        ))}

        {/* 
          Add placeholder items to balance the last row.
          These items are invisible and only occupy space to fill columns.
        */}
        {Array.from({ length: placeholdersNeeded }).map((_, index) => (
          <div
            key={`placeholder-${index}`}
            className="mb-4 break-inside-avoid invisible"
          >
            {/* Empty div to fill the grid */}
          </div>
        ))}
      </Masonry>

      {/* 
        Inline CSS Styles for react-masonry-css.
        These styles define the layout and gutter (spacing between columns).
      */}
      <style>{`
        /* 
          .my-masonry-grid defines the grid container as a flex container 
          with negative left margin to offset the padding applied to columns.
        */
        .my-masonry-grid {
          display: flex;
          margin-left: -1rem; /* Gutter size offset (matches the column padding) */
          width: auto;
        }

        /* 
          .my-masonry-grid_column defines each column within the masonry grid.
          It applies left padding to create gutters between columns.
        */
        .my-masonry-grid_column {
          padding-left: 1rem; /* Gutter size */
          background-clip: padding-box; /* Ensures background doesn't bleed into gutters */
        }

        /* 
          .break-inside-avoid prevents items from breaking between columns,
          ensuring each item stays intact within its column.
        */
        .break-inside-avoid {
          break-inside: avoid;
        }

        /* 
          Media Query for Smaller Screens
          Adjusts gutter sizes for screens narrower than 768px to maintain responsiveness.
        */
        @media (max-width: 767px) {
          .my-masonry-grid {
            margin-left: -0.75rem; /* Adjust gutter size offset */
          }
          .my-masonry-grid_column {
            padding-left: 0.75rem; /* Adjust gutter size */
          }
        }

        /* 
          Fade-in effect for images once they are loaded.
          Improves perceived performance and user experience.
        */
        img.loaded {
          {/* opacity: 1; */}
        }

        img:not(.loaded) {
          {/* opacity: 0; */}
          transition: opacity 0.5s ease-in;
        }
      `}</style>
    </div>
  );
};

export default MasonryGrid;
